import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, Lock, ArrowLeft } from "lucide-react";
import EditableText from "../components/EditableText";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);

  const redirected = useRef(false);
  useEffect(() => {
    if (!user || redirected.current) return;
    redirected.current = true;
    let returnTo = null;
    try {
      const rt = JSON.parse(sessionStorage.getItem("dhdh_return_to") || "null");
      if (rt && (!rt.uid || rt.uid === user.user_id)) returnTo = rt.path;
    } catch { returnTo = null; }
    sessionStorage.removeItem("dhdh_return_to");
    if (user.must_change_password) navigate("/profile?tab=password", { replace: true });
    else if (returnTo && returnTo !== "/login") navigate(returnTo, { replace: true });
    else if (user.requires_choice) navigate("/membership/choose", { replace: true });
    else if (user.verification_status !== "verified" && !user.is_admin) navigate("/verification", { replace: true });
    else navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const resendVerification = async () => {
    try {
      const { data } = await axios.post(`${API}/auth/resend-verification`, { email: unverifiedEmail });
      toast.success(data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't resend"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    setUnverifiedEmail(null);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      // Redirect is handled by the `if (user)` branch above (return-to, forced password change, etc.).
    } catch (error) {
      // Show a precise error so we can diagnose. The previous generic
      // "Something went wrong" message hid the real cause.
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      let msg;
      if (status === 403 && detail?.code === "EMAIL_NOT_VERIFIED") {
        setUnverifiedEmail(email);
        msg = detail.msg;
      } else if (status === 401 || status === 403) {
        msg = "Incorrect email or password";
      } else if (status === 429) {
        msg = "Too many attempts. Please wait a few minutes and try again.";
      } else if (status >= 500) {
        msg = `Server error (${status}). Please try again or contact support.`;
      } else if (!error.response) {
        // Network-level failure (CORS, offline, blocked by extension, mixed content)
        msg = `Network error: couldn't reach the server. Check your connection or browser settings. (${error.message || "no response"})`;
      } else {
        msg = formatApiErrorDetail(detail) || `Login failed (${status})`;
      }
      toast.error(msg, { duration: 6000 });
      // eslint-disable-next-line no-console
      console.error("[Login] failed:", { status, detail, message: error.message, response: error.response });
    } finally {
      setLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="inline-flex items-center text-[#64748B] hover:text-[#6B4E8A] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="mb-8">
            <EditableText
              blockKey="login.title"
              fallback="Welcome back"
              as="h1"
              className="text-3xl font-medium text-[#6B4E8A] mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            />
            <EditableText
              blockKey="login.subtitle"
              fallback="Sign in to your DHDH account"
              as="p"
              className="text-[#64748B]"
              multiline
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#6B4E8A] font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-12 py-6 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#6B4E8A] font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-12 py-6 bg-white border-[#E5DFEC] focus:border-[#6B4E8A] focus:ring-1 focus:ring-[#6B4E8A] rounded-xl"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-[#6B4E8A] hover:underline font-medium"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full py-6 text-base font-medium"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
            {unverifiedEmail && (
              <div className="rounded-xl border border-[#FCD34D] bg-[#FFFBEB] p-3 text-sm text-[#92400E]" data-testid="login-unverified-notice">
                Your email isn't verified yet. Check your inbox for the link (it expires after 24 hours).
                <button type="button" onClick={resendVerification} className="block mt-2 font-medium underline" data-testid="login-resend-verification-btn">
                  Send a new verification link
                </button>
              </div>
            )}
          </form>

          <p className="text-center text-[#64748B] mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#8DB5A0] hover:text-[#D0694E] font-medium" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:flex flex-1 bg-[#6B4E8A] items-center justify-center p-16">
        <div className="max-w-md text-center">
          <img src="/dhdh-logo.png" alt="DHDH" className="w-32 h-32 rounded-full object-cover mx-auto mb-8 ring-4 ring-white/30" />
          <h2 className="text-3xl font-medium text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dental Hygiene Development Hub
          </h2>
          <p className="text-[#94A3B8]">
            The trusted community platform for registered dental hygienists across Canada.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
